import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { credentialLabel } from "@/lib/labels";
import { FileStack, ArrowRight } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { FilterPanel } from "@/components/admin/FilterPanel";
import { BulkActionsBar } from "@/components/admin/BulkActionsBar";
import { useFilters } from "@/lib/useFilters";
import { useSelection } from "@/lib/useSelection";

const APP_FILTERS = [
  { key: "search", type: "search", label: "Search name, email, or credential type", paths: ["applicant_name", "applicant_email", "credential_type"] },
  { key: "status", type: "select", label: "Status", path: "status",
    options: [
      { value: "submitted", label: "Submitted" },
      { value: "under_review", label: "Under review" },
      { value: "info_requested", label: "Info requested" },
      { value: "approved", label: "Approved" },
      { value: "denied", label: "Denied" },
      { value: "draft", label: "Draft" },
    ] },
  { key: "organization", type: "select", label: "Board", path: "organization",
    options: [{ value: "ACIS", label: "ACIS" }, { value: "IBTHM", label: "IBTHM" }] },
  { key: "credential_type", type: "select", label: "Credential", path: "credential_type",
    options: [
      { value: "BCT", label: "BCT" }, { value: "CHTC", label: "CHTC" },
      { value: "CTS", label: "CTS" }, { value: "LHT", label: "LHT" },
    ] },
  { key: "payment_status", type: "select", label: "Payment", path: "payment.status",
    options: [
      { value: "unpaid", label: "Unpaid" },
      { value: "pending_verification", label: "Pending verification" },
      { value: "verified", label: "Verified" },
      { value: "refunded", label: "Refunded" },
    ] },
  { key: "date_from", type: "dateFrom", label: "Submitted from", path: "created_at" },
  { key: "date_to", type: "dateTo", label: "Submitted to", path: "created_at" },
  { key: "is_archived", type: "boolean", label: "Archived", path: "is_archived" },
  { key: "is_sample", type: "boolean", label: "Sample", path: "is_sample" },
  { key: "is_deleted", type: "boolean", label: "Deleted", path: "is_deleted" },
];

export default function Applications() {
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchApps = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/applications");
      setApps(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchApps(); }, []);

  const filters = useFilters(apps, APP_FILTERS, {
    defaultSort: { field: "created_at", dir: "desc" },
  });
  const selection = useSelection(filters.filtered);
  const isAdmin = user.role === "admin";
  const isStaff = isAdmin || user.role === "reviewer" || user.role === "credentialing_reviewer" || user.role === "accreditation_reviewer" || user.role === "read_only_auditor";

  return (
    <div data-testid="applications-page" className="animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">{isStaff ? "Review queue" : "My applications"}</div>
          <h1 className="font-heading text-3xl font-bold text-primary">Applications</h1>
        </div>
        {!isStaff && (
          <Link to="/dashboard/applications/new">
            <Button className="bg-primary text-white hover:bg-[#113A64]" data-testid="new-application-btn">New application</Button>
          </Link>
        )}
      </div>

      <FilterPanel
        schemas={APP_FILTERS}
        values={filters.values}
        set={filters.set}
        clear={filters.clear}
        count={filters.count}
        total={apps.length}
        activeCount={filters.activeCount}
        sortFields={[
          { key: "created_at", label: "Submitted date" },
          { key: "applicant_name", label: "Applicant name" },
          { key: "status", label: "Status" },
        ]}
        sort={filters.sort}
        setSort={filters.setSort}
        testid="apps-filters"
      />

      {isAdmin && <BulkActionsBar collection="applications" selection={selection} onDone={fetchApps} />}

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">
              <tr>
                {isAdmin && (
                  <th className="p-4 w-10">
                    <Checkbox checked={selection.allSelected} onCheckedChange={selection.toggleAll} data-testid="apps-select-all" />
                  </th>
                )}
                <th className="p-4">Applicant</th>
                <th className="p-4">Credential</th>
                <th className="p-4">Status</th>
                <th className="p-4">Payment</th>
                <th className="p-4">Submitted</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={isAdmin ? 7 : 6} className="p-8 text-center text-slate-500">Loading…</td></tr>
              )}
              {!loading && filters.filtered.length === 0 && (
                <tr>
                  <td colSpan={isAdmin ? 7 : 6} className="p-12">
                    <div className="text-center">
                      <FileStack className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                      <div className="font-heading font-semibold text-primary">No applications</div>
                      <div className="text-sm text-slate-500 mt-1">Try adjusting the filters or create a new application.</div>
                    </div>
                  </td>
                </tr>
              )}
              {filters.filtered.map((a) => (
                <tr key={a.id} className="hover:bg-slate-50" data-testid={`app-row-${a.id}`}>
                  {isAdmin && (
                    <td className="p-4">
                      <Checkbox checked={selection.isSelected(a.id)} onCheckedChange={() => selection.toggle(a.id)} data-testid={`app-select-${a.id}`} />
                    </td>
                  )}
                  <td className="p-4">
                    <div className="font-medium text-primary">{a.applicant_name}</div>
                    <div className="text-xs text-slate-500">{a.applicant_email}</div>
                    {a.is_deleted && <span className="text-[9px] uppercase tracking-wider text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded mt-1 inline-block">Deleted</span>}
                  </td>
                  <td className="p-4">
                    <div className="font-mono-data text-xs text-primary" title={a.credential_type}>{credentialLabel(a.credential_type)}</div>
                    <div className="text-xs text-slate-500">{a.organization}</div>
                  </td>
                  <td className="p-4"><StatusBadge status={a.status} /></td>
                  <td className="p-4"><PaymentBadge p={a.payment} /></td>
                  <td className="p-4 text-slate-600 text-xs">{new Date(a.created_at).toLocaleDateString()}</td>
                  <td className="p-4 text-right">
                    <Link to={`/dashboard/applications/${a.id}`}>
                      <Button variant="ghost" size="sm" className="text-primary">Open <ArrowRight className="ml-1.5 h-3.5 w-3.5" /></Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    draft: "bg-slate-100 text-slate-700 border-slate-200",
    submitted: "bg-blue-50 text-blue-800 border-blue-200",
    under_review: "bg-amber-50 text-amber-800 border-amber-200",
    info_requested: "bg-orange-50 text-orange-800 border-orange-200",
    approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
    denied: "bg-red-50 text-red-800 border-red-200",
  };
  return <Badge variant="outline" className={`capitalize ${map[status] || map.draft}`}>{(status || "").replace("_", " ")}</Badge>;
}

function PaymentBadge({ p }) {
  const status = p?.status || "unpaid";
  const map = {
    unpaid: "bg-slate-100 text-slate-700 border-slate-200",
    pending_verification: "bg-amber-50 text-amber-800 border-amber-200",
    verified: "bg-emerald-50 text-emerald-800 border-emerald-200",
    refunded: "bg-slate-100 text-slate-600 border-slate-200",
  };
  return <Badge variant="outline" className={`capitalize ${map[status]}`}>{status.replace("_", " ")}</Badge>;
}
